import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace, useExpenses } from '@/hooks/useData';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/Toast';
import { toIsoDate } from '@/lib/format';
import { uploadFileToDrive, isDriveConnected, DriveNotConnectedError, DriveAuthRevokedError } from '@/lib/driveUpload';
import { compressImage } from '@/lib/imageCompress';
import { TodueWordmark } from '@/components/Logo';
import GlobalSearchResults from '@/components/GlobalSearchResults';
import { SECTIONS as CENTRAL_SECTIONS } from '@/lib/sections';
import { FEATURES } from '@/lib/featureFlags';
import './MobileTopbar.css';

// Single source of truth: derive mobile menu from @/lib/sections.
// Excluded: 'prehlad' (homed via todue logo button), 'nastavenia' (via ⚙ on card).
// Prepended: 'search' (mobile-only reveal pattern).
const SECTIONS = [
  { id: 'search', label: 'Hľadať', color: 'blue', icon: 'search', action: 'search' },
  ...CENTRAL_SECTIONS
    .filter(s => !['prehlad', 'nastavenia'].includes(s.id))
    .filter(s => !s.flag || FEATURES[s.flag]),
];

// ── Capture (+) items — quick actions
const CAPTURE = [
  { id: 'qr',    label: 'Sken QR bločku', color: 'yellow', icon: 'qr',     action: 'qr'     },
  { id: 'photo', label: 'Odfoť doklad',   color: 'blue',   icon: 'camera', action: 'photo'  },
  { id: 'fa',    label: 'Nová faktúra',   color: 'green',  icon: 'doc',    action: 'newfa'  },
];

const ICONS = {
  search:  '<circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>',
  doc:     '<path d="M7 3h10v18H7zM10 8h7M10 12h7M10 16h5"/>',
  receipt: '<path d="M6 3h12v18l-3-2-3 2-3-2-3 2zM9 8h6M9 12h6M9 16h4"/>',
  folder:  '<path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>',
  users:   '<circle cx="9" cy="8" r="3"/><circle cx="17" cy="9" r="2.5"/><path d="M3 19c0-3 3-5 6-5s6 2 6 5M15 19c0-2 1-3.5 3-3.5s3 1.5 3 3.5"/>',
  archive: '<rect x="4" y="4" width="16" height="5" rx="1"/><path d="M5 9v11h14V9M9 13h6"/>',
  gear:    '<circle cx="12" cy="12" r="3.2"/><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2"/>',
  qr:      '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 14h3v3M21 17v4M17 21h4"/>',
  camera:  '<path d="M3 8l2-3h14l2 3v11H3z"/><circle cx="12" cy="13" r="3.5"/>',
  euro:    '<path d="M18 6a7 7 0 1 0 0 12M3 10h10M3 14h9"/>',
  box:     '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>',
};

// Vertical dropdown layout (Daniel feedback 2026-05-17 — fan replaced with column).
// Anchor: under button, x=0, y = HEAD + i*SPACING. Easing/transition ostávajú rovnaké.
function fanLayout(n) {
  const SPACING = 62;
  const HEAD = 56; // gap medzi anchor button-om a prvou položkou
  const ICON = 46;
  const FONT = 17;
  const pts = [];
  for (let i = 0; i < n; i++) pts.push({ x: 0, y: HEAD + i * SPACING });
  return { pts, ICON, FONT };
}

export default function MobileTopbar() {
  const { workspace } = useWorkspace();
  const { user } = useAuth();
  const { create: createExpense } = useExpenses(workspace?.id);
  const navigate = useNavigate();
  const toast = useToast();

  const [menuOpen, setMenuOpen] = useState(false);
  const [capOpen, setCapOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const menuBtnRef = useRef(null);
  const capBtnRef = useRef(null);
  const fileRef = useRef(null);
  const searchInputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const closeAll = useCallback(() => {
    setMenuOpen(false);
    setCapOpen(false);
    setSearchOpen(false);
  }, []);

  // ESC closes
  useEffect(() => {
    if (!menuOpen && !capOpen && !searchOpen) return;
    const onKey = (e) => e.key === 'Escape' && closeAll();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen, capOpen, searchOpen, closeAll]);

  // Auto-focus search input when opened
  useEffect(() => {
    if (searchOpen) {
      const t = setTimeout(() => searchInputRef.current?.focus(), 280);
      return () => clearTimeout(t);
    }
    setSearchQuery('');
  }, [searchOpen]);

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setCapOpen(false);
    setSearchOpen(false);
    setMenuOpen((v) => !v);
  };
  const handleCapClick = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    setSearchOpen(false);
    setCapOpen((v) => !v);
  };
  const handleHome = () => {
    closeAll();
    navigate('/');
  };

  const handleSectionPick = (item) => {
    if (item.action === 'search') {
      setMenuOpen(false);
      setCapOpen(false);
      setSearchOpen(true);
      return;
    }
    closeAll();
    if (item.path) navigate(item.path);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearchOpen(false);
    toast.show?.('Hľadanie zatiaľ neimplementované — pripravujeme.');
  };

  const handleCapturePick = (item) => {
    closeAll();
    if (item.action === 'newfa') {
      navigate('/faktury/nova');
      return;
    }
    if (item.action === 'photo' || item.action === 'qr') {
      // both trigger camera file picker; in real impl QR could route to scanner modal
      fileRef.current?.click();
    }
  };

  // Capture file → Drive upload + create expense (mirror QuickCaptureFab)
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !workspace || !user) return;
    setBusy(true);
    try {
      const connected = await isDriveConnected();
      if (!connected) {
        toast.error('Najprv pripoj Google Drive v Nastaveniach.');
        return;
      }
      const compressed = await compressImage(file);
      const uploadFile = compressed.file;
      const result = await uploadFileToDrive({
        file: uploadFile,
        workspace_id: workspace.id,
        year: new Date().getFullYear(),
        type: 'vydavky',
      });
      await createExpense({
        vendor_name: null, vendor_ico: null, amount: 0,
        dph_rate: workspace.default_dph_rate || 0, dph_amount: 0,
        date: toIsoDate(), category: 'other',
        description: 'Rýchly záber — doplň polia.',
        paid_via: 'card',
        gdrive_file_id: result.gdrive_file_id,
      });
      toast.success('Doklad uložený do Drive. Polia doplníš v sekcii Výdavky.');
    } catch (err) {
      if (err instanceof DriveNotConnectedError || err instanceof DriveAuthRevokedError) {
        toast.error(err.message);
      } else {
        toast.error('Upload zlyhal: ' + (err.message || err));
      }
    } finally {
      setBusy(false);
    }
  };

  const open = menuOpen || capOpen || searchOpen;

  return (
    <div className={`mt-root ${open ? 'is-open' : ''} ${searchOpen ? 'is-search' : ''}`}>
      <div className="mt-backdrop" onClick={closeAll} aria-hidden="true" />

      <div className="mt-bar">
        <button
          ref={menuBtnRef}
          type="button"
          className={`mt-cbtn mt-menu-btn ${menuOpen ? 'is-open' : ''}`}
          onClick={handleMenuClick}
          aria-label="Menu"
        >
          <svg viewBox="0 0 24 24">
            <line className="ln-a" x1="4" y1="6.5" x2="20" y2="6.5" />
            <line className="ln-b" x1="4" y1="12"  x2="20" y2="12"  />
            <line className="ln-c" x1="4" y1="17.5" x2="20" y2="17.5" />
          </svg>
        </button>

        <button type="button" className="mt-home" onClick={handleHome} aria-label="Domov">
          <TodueWordmark height={28} />
        </button>

        <button
          ref={capBtnRef}
          type="button"
          className={`mt-cbtn mt-cap-btn ${capOpen ? 'is-open' : ''}`}
          onClick={handleCapClick}
          aria-label="Pridať / skenovať"
          disabled={busy}
        >
          <svg viewBox="0 0 24 24">
            <line x1="12" y1="4" x2="12" y2="20" />
            <line x1="4" y1="12" x2="20" y2="12" />
          </svg>
        </button>
      </div>

      <Fan
        items={SECTIONS}
        anchorRef={menuBtnRef}
        active={menuOpen}
        dir="r"
        onPick={handleSectionPick}
      />
      <Fan
        items={CAPTURE}
        anchorRef={capBtnRef}
        active={capOpen}
        dir="l"
        onPick={handleCapturePick}
      />

      <form
        className={`mt-search ${searchOpen ? 'is-open' : ''}`}
        onSubmit={handleSearchSubmit}
        onClick={(e) => e.stopPropagation()}
        role="search"
      >
        <svg className="mt-search-ico" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
        <input
          ref={searchInputRef}
          type="search"
          className="mt-search-input"
          placeholder="Hľadať klientov, faktúry, doklady…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoComplete="off"
        />
        <button
          type="button"
          className="mt-search-close"
          onClick={() => setSearchOpen(false)}
          aria-label="Zavrieť vyhľadávanie"
        >
          <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      </form>

      {searchOpen && (
        <div className="mt-search-results-wrap" onClick={(e) => e.stopPropagation()}>
          <GlobalSearchResults
            query={searchQuery}
            onPick={() => setSearchOpen(false)}
            variant="panel"
          />
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleFile}
      />
    </div>
  );
}

function Fan({ items, anchorRef, active, dir, onPick }) {
  const [origin, setOrigin] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!active) return;
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setOrigin({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
  }, [active, anchorRef]);

  const layout = fanLayout(items.length);
  const mirror = dir === 'l' ? -1 : 1;

  return (
    <div className={`mt-fan ${active ? 'is-active' : ''}`}
      style={{ left: origin.x, top: origin.y }}
    >
      {items.map((it, i) => {
        const p = layout.pts[i];
        return (
          <button
            key={it.id}
            type="button"
            className={`mt-fan-item dir-${dir} ${it.color}`}
            style={{
              '--isz': `${layout.ICON}px`,
              '--x':   `${(mirror * p.x).toFixed(1)}px`,
              '--y':   `${p.y.toFixed(1)}px`,
              '--in-d':  `${i * 36}ms`,
              '--out-d': `${(items.length - 1 - i) * 36}ms`,
            }}
            onClick={(e) => { e.stopPropagation(); onPick(it); }}
            aria-label={it.label}
          >
            <span className="fi-circle">
              <svg viewBox="0 0 24 24" dangerouslySetInnerHTML={{ __html: ICONS[it.icon] || '' }} />
            </span>
            <span className="fi-lab" style={{ fontSize: `${layout.FONT.toFixed(1)}px` }}>
              {it.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
